import { useState, useEffect, useCallback } from 'react';

interface SearchItem {
  [key: string]: any;
  role?: string;
}

interface UseSearchItemProps<T extends SearchItem> {
  items: T[];
  searchTerm: string;
  selectedRole?: string;
  searchFields?: (keyof T)[];
}

const useSearchItem = <T extends SearchItem>({
  items,
  searchTerm,
  selectedRole = 'all',
  searchFields,
}: UseSearchItemProps<T>): T[] => {
  const [filteredItems, setFilteredItems] = useState<T[]>(items);

  const filterItems = useCallback((): void => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    const temp = items.filter((item: T) => {
      const propertiesToSearch = searchFields 
        ? searchFields.map(field => String(item[field]).toLowerCase())
        : Object.values(item).map(value => String(value).toLowerCase());

      return (
        (selectedRole === 'all' || item.role === selectedRole) &&
        propertiesToSearch.some(property => 
          property.includes(lowerCaseSearchTerm)
        )
      );
    });

    setFilteredItems(temp);
  }, [items, searchTerm, selectedRole, searchFields]);

  useEffect(() => {
    if (searchTerm.length > 0) {
      filterItems();
    } else {
      setFilteredItems(items);
    }
  }, [items, filterItems, searchTerm]);

  return filteredItems;
};

export default useSearchItem;